import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, CheckCircleIcon, ThumbsUpIcon, ThumbsDownIcon, CalendarIcon, FileTextIcon, AlertTriangleIcon, ClockIcon, PenToolIcon } from 'lucide-react';
interface CompleteViewingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    clientLikes: boolean;
    feedback: string;
    nextStep: 'awaiting_response' | 'schedule_signing' | 'rematching';
  }) => void;
  viewing: any;
}
export function CompleteViewingModal({
  isOpen,
  onClose,
  onSubmit,
  viewing
}: CompleteViewingModalProps) {
  const [step, setStep] = useState<'decision' | 'next_step' | 'feedback'>('decision');
  const [clientLikes, setClientLikes] = useState<boolean | null>(null);
  const [nextStep, setNextStep] = useState<'awaiting_response' | 'schedule_signing' | null>(null);
  const [feedback, setFeedback] = useState('');
  const handleDecision = (likes: boolean) => {
    setClientLikes(likes);
    if (likes) {
      setStep('next_step');
    } else {
      setStep('feedback');
      setNextStep(null);
    }
  };
  const handleNextStepSelection = (step: 'awaiting_response' | 'schedule_signing') => {
    setNextStep(step);
    // If "Programare semnare" is selected, skip feedback and submit directly
    if (step === 'schedule_signing') {
      // Check for missing contracts
      const missingContracts = [];
      if (viewing?.clientContractSigned && viewing?.clientSignPhysically && !viewing?.clientContractFile) {
        missingContracts.push('Contract Client');
      }
      if (viewing?.ownerContractSigned && viewing?.ownerSignPhysically && !viewing?.ownerContractFile) {
        missingContracts.push('Contract Proprietar');
      }
      if (missingContracts.length > 0) {
        alert(`⚠️ Contracte lipsă: ${missingContracts.join(', ')}\n\nTe rog încarcă contractele înainte de a finaliza vizionarea.`);
        return;
      }
      // Submit directly without feedback step
      onSubmit({
        clientLikes: true,
        feedback: 'Clientul dorește să programeze semnarea contractului.',
        nextStep: 'schedule_signing'
      });
      // Reset
      setStep('decision');
      setClientLikes(null);
      setNextStep(null);
      setFeedback('');
    } else {
      // For "Aștept răspuns", continue to feedback step
      setStep('feedback');
    }
  };
  const handleSubmit = () => {
    if (clientLikes === null) {
      alert('Te rog selectează dacă clientului îi place sau nu proprietatea');
      return;
    }
    if (clientLikes && !nextStep) {
      alert('Te rog selectează următorul pas');
      return;
    }
    if (!feedback.trim()) {
      alert('Te rog adaugă feedback despre vizionare');
      return;
    }
    // Check for missing contracts
    const missingContracts = [];
    if (viewing?.clientContractSigned && viewing?.clientSignPhysically && !viewing?.clientContractFile) {
      missingContracts.push('Contract Client');
    }
    if (viewing?.ownerContractSigned && viewing?.ownerSignPhysically && !viewing?.ownerContractFile) {
      missingContracts.push('Contract Proprietar');
    }
    if (missingContracts.length > 0) {
      alert(`⚠️ Contracte lipsă: ${missingContracts.join(', ')}\n\nTe rog încarcă contractele înainte de a finaliza vizionarea.`);
      return;
    }
    onSubmit({
      clientLikes,
      feedback,
      nextStep: clientLikes ? nextStep! : 'rematching'
    });
    // Reset
    setStep('decision');
    setClientLikes(null);
    setNextStep(null);
    setFeedback('');
  };
  const handleBack = () => {
    if (step === 'feedback') {
      if (clientLikes) {
        setStep('next_step');
      } else {
        setStep('decision');
        setClientLikes(null);
      }
    } else if (step === 'next_step') {
      setStep('decision');
      setClientLikes(null);
      setNextStep(null);
    } else {
      onClose();
    }
  };
  // Check if there are missing contracts
  const hasMissingContracts = viewing?.clientContractSigned && viewing?.clientSignPhysically && !viewing?.clientContractFile || viewing?.ownerContractSigned && viewing?.ownerSignPhysically && !viewing?.ownerContractFile;
  return <AnimatePresence data-id="element-3022">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" data-id="element-3023" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-3024">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden" data-id="element-3025">
              {/* Header */}
              <div className="bg-gradient-to-r from-emerald-500 to-green-600 text-white px-6 py-5" data-id="element-3026">
                <div className="flex items-center justify-between" data-id="element-3027">
                  <div className="flex items-center space-x-3" data-id="element-3028">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm" data-id="element-3029">
                      <CheckCircleIcon className="w-6 h-6" data-id="element-3030" />
                    </div>
                    <div data-id="element-3031">
                      <h3 className="text-xl font-bold" data-id="element-3032">
                        Finalizează Vizionare
                      </h3>
                      <p className="text-sm text-emerald-100" data-id="element-3033">
                        {viewing?.clientName} - {viewing?.propertyId}
                      </p>
                    </div>
                  </div>
                  <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors" data-id="element-3034">
                    <XIcon className="w-6 h-6" data-id="element-3035" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6" data-id="element-3036">
                {/* Missing Contracts Warning */}
                {hasMissingContracts && <div className="mb-6 bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-xl" data-id="element-3037">
                    <div className="flex items-start space-x-3" data-id="element-3038">
                      <AlertTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-3039" />
                      <div data-id="element-3040">
                        <p className="text-sm font-bold text-amber-900 mb-1" data-id="element-3041">
                          ⚠️ Contracte Lipsă
                        </p>
                        <p className="text-sm text-amber-700" data-id="element-3042">
                          Ai bifat "Semnez Fizic" dar nu ai încărcat
                          contractele. Te rog încarcă-le înainte de finalizare.
                        </p>
                      </div>
                    </div>
                  </div>}

                <AnimatePresence mode="wait" data-id="element-3043">
                  {/* STEP 1: Decision */}
                  {step === 'decision' && <motion.div key="decision" initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: 20
              }} className="space-y-6" data-id="element-3044">
                      <div className="text-center" data-id="element-3045">
                        <h4 className="text-2xl font-bold text-[#132C53] mb-2" data-id="element-3046">
                          Clientului îi place proprietatea?
                        </h4>
                        <p className="text-slate-600" data-id="element-3047">
                          Selectează răspunsul pentru a continua
                        </p>
                      </div>

                      <div className="grid grid-cols-2 gap-4" data-id="element-3048">
                        {/* ÎI PLACE */}
                        <button onClick={() => handleDecision(true)} className="group relative overflow-hidden rounded-2xl border-2 border-emerald-200 hover:border-emerald-500 transition-all p-8 bg-gradient-to-br from-emerald-50 to-green-50 hover:shadow-xl" data-id="element-3049">
                          <div className="flex flex-col items-center space-y-4" data-id="element-3050">
                            <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-green-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform" data-id="element-3051">
                              <ThumbsUpIcon className="w-10 h-10 text-white" data-id="element-3052" />
                            </div>
                            <div data-id="element-3053">
                              <p className="text-xl font-bold text-emerald-900 mb-1" data-id="element-3054">
                                Îi Place
                              </p>
                              <p className="text-sm text-emerald-700" data-id="element-3055">
                                → Următorul pas
                              </p>
                            </div>
                          </div>
                        </button>

                        {/* NU ÎI PLACE */}
                        <button onClick={() => handleDecision(false)} className="group relative overflow-hidden rounded-2xl border-2 border-slate-200 hover:border-slate-400 transition-all p-8 bg-gradient-to-br from-slate-50 to-slate-100 hover:shadow-xl" data-id="element-3056">
                          <div className="flex flex-col items-center space-y-4" data-id="element-3057">
                            <div className="w-20 h-20 bg-gradient-to-br from-slate-500 to-slate-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform" data-id="element-3058">
                              <ThumbsDownIcon className="w-10 h-10 text-white" data-id="element-3059" />
                            </div>
                            <div data-id="element-3060">
                              <p className="text-xl font-bold text-slate-900 mb-1" data-id="element-3061">
                                Nu Îi Place
                              </p>
                              <p className="text-sm text-slate-700" data-id="element-3062">
                                → Rematching client
                              </p>
                            </div>
                          </div>
                        </button>
                      </div>
                    </motion.div>}

                  {/* STEP 2: Next Step Selection (only if client likes) */}
                  {step === 'next_step' && <motion.div key="next_step" initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -20
              }} className="space-y-6" data-id="element-3063">
                      <div className="text-center" data-id="element-3064">
                        <h4 className="text-2xl font-bold text-[#132C53] mb-2" data-id="element-3065">
                          Care este următorul pas?
                        </h4>
                        <p className="text-slate-600" data-id="element-3066">
                          Selectează acțiunea potrivită
                        </p>
                      </div>

                      <div className="space-y-3" data-id="element-3067">
                        {/* AȘTEPT RĂSPUNS */}
                        <button onClick={() => handleNextStepSelection('awaiting_response')} className="group w-full relative overflow-hidden rounded-2xl border-2 border-blue-200 hover:border-blue-500 transition-all p-6 bg-gradient-to-br from-blue-50 to-indigo-50 hover:shadow-xl text-left" data-id="element-3068">
                          <div className="flex items-center space-x-4" data-id="element-3069">
                            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform flex-shrink-0" data-id="element-3070">
                              <ClockIcon className="w-8 h-8 text-white" data-id="element-3071" />
                            </div>
                            <div className="flex-1" data-id="element-3072">
                              <p className="text-lg font-bold text-blue-900 mb-1" data-id="element-3073">
                                Aștept Răspuns
                              </p>
                              <p className="text-sm text-blue-700" data-id="element-3074">
                                Clientul trebuie să se gândească / să consulte
                                familia
                              </p>
                            </div>
                          </div>
                        </button>

                        {/* PROGRAMARE SEMNARE */}
                        <button onClick={() => handleNextStepSelection('schedule_signing')} className="group w-full relative overflow-hidden rounded-2xl border-2 border-emerald-200 hover:border-emerald-500 transition-all p-6 bg-gradient-to-br from-emerald-50 to-green-50 hover:shadow-xl text-left" data-id="element-3075">
                          <div className="flex items-center space-x-4" data-id="element-3076">
                            <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform flex-shrink-0" data-id="element-3077">
                              <PenToolIcon className="w-8 h-8 text-white" data-id="element-3078" />
                            </div>
                            <div className="flex-1" data-id="element-3079">
                              <p className="text-lg font-bold text-emerald-900 mb-1" data-id="element-3080">
                                Programare Semnare
                              </p>
                              <p className="text-sm text-emerald-700" data-id="element-3081">
                                Clientul este gata să semneze contractul
                              </p>
                            </div>
                          </div>
                        </button>
                      </div>
                    </motion.div>}

                  {/* STEP 3: Feedback */}
                  {step === 'feedback' && <motion.div key="feedback" initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -20
              }} className="space-y-4" data-id="element-3082">
                      {/* Decision Summary */}
                      <div className={`rounded-xl p-4 border-l-4 ${clientLikes ? nextStep === 'awaiting_response' ? 'bg-blue-50 border-blue-500' : 'bg-emerald-50 border-emerald-500' : 'bg-slate-50 border-slate-500'}`} data-id="element-3083">
                        <div className="flex items-center space-x-3" data-id="element-3084">
                          {clientLikes ? nextStep === 'awaiting_response' ? <ClockIcon className="w-6 h-6 text-blue-600" data-id="element-3085" /> : <PenToolIcon className="w-6 h-6 text-emerald-600" data-id="element-3086" /> : <ThumbsDownIcon className="w-6 h-6 text-slate-600" data-id="element-3087" />}
                          <div data-id="element-3088">
                            <p className={`font-bold ${clientLikes ? nextStep === 'awaiting_response' ? 'text-blue-900' : 'text-emerald-900' : 'text-slate-900'}`} data-id="element-3089">
                              {clientLikes ? nextStep === 'awaiting_response' ? 'Clientul așteaptă să se decidă' : 'Programare semnare contract' : 'Clientului nu îi place proprietatea'}
                            </p>
                            <p className={`text-sm ${clientLikes ? nextStep === 'awaiting_response' ? 'text-blue-700' : 'text-emerald-700' : 'text-slate-700'}`} data-id="element-3090">
                              {clientLikes ? nextStep === 'awaiting_response' ? 'Status: Aștept răspuns client' : 'Următorul pas: Programare semnare' : 'Următorul pas: Rematching pentru alte proprietăți'}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Feedback */}
                      <div data-id="element-3091">
                        <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-3092">
                          Feedback Vizionare *
                        </label>
                        <textarea value={feedback} onChange={e => setFeedback(e.target.value)} rows={5} placeholder={clientLikes ? nextStep === 'awaiting_response' ? 'ex: Clientul este interesat dar vrea să discute cu familia. Urmează să ne contacteze în 2-3 zile...' : 'ex: Clientul este foarte mulțumit de proprietate. Apreciază locația și finisajele. Dorește să semneze contractul cât mai curând...' : 'ex: Clientul consideră că proprietatea este prea mică. Caută ceva mai spațios în aceeași zonă...'} className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none" data-id="element-3093" />
                      </div>

                      {/* Next Steps Info */}
                      {clientLikes ? nextStep === 'awaiting_response' ? <div className="bg-blue-50 rounded-xl p-4 border border-blue-200" data-id="element-3094">
                            <div className="flex items-start space-x-3" data-id="element-3095">
                              <ClockIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-3096" />
                              <div data-id="element-3097">
                                <p className="text-sm font-bold text-blue-900 mb-1" data-id="element-3098">
                                  Următorul Pas
                                </p>
                                <p className="text-sm text-blue-700" data-id="element-3099">
                                  Vizionarea va fi marcată ca "Aștept răspuns".
                                  Vei putea ulterior să programezi semnarea sau
                                  să anulezi dacă clientul refuză.
                                </p>
                              </div>
                            </div>
                          </div> : <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200" data-id="element-3100">
                            <div className="flex items-start space-x-3" data-id="element-3101">
                              <CalendarIcon className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" data-id="element-3102" />
                              <div data-id="element-3103">
                                <p className="text-sm font-bold text-emerald-900 mb-1" data-id="element-3104">
                                  Următorul Pas
                                </p>
                                <p className="text-sm text-emerald-700" data-id="element-3105">
                                  După finalizare, vei putea programa semnarea
                                  contractului și indexa datele finale pentru
                                  generarea contractului proprietar-chiriaș.
                                </p>
                              </div>
                            </div>
                          </div> : <div className="bg-amber-50 rounded-xl p-4 border border-amber-200" data-id="element-3106">
                          <div className="flex items-start space-x-3" data-id="element-3107">
                            <FileTextIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-3108" />
                            <div data-id="element-3109">
                              <p className="text-sm font-bold text-amber-900 mb-1" data-id="element-3110">
                                Următorul Pas
                              </p>
                              <p className="text-sm text-amber-700" data-id="element-3111">
                                Clientul va fi redirecționat către procesul de
                                rematching pentru a găsi alte proprietăți
                                potrivite criteriilor sale.
                              </p>
                            </div>
                          </div>
                        </div>}
                    </motion.div>}
                </AnimatePresence>
              </div>

              {/* Footer */}
              <div className="bg-slate-50 px-6 py-4 flex items-center justify-between border-t border-slate-200" data-id="element-3112">
                <button onClick={handleBack} className="px-5 py-2.5 rounded-xl font-semibold text-slate-700 hover:bg-slate-200 transition-colors" data-id="element-3113">
                  {step === 'decision' ? 'Anulează' : 'Înapoi'}
                </button>
                {step === 'feedback' && <button onClick={handleSubmit} className="px-6 py-2.5 rounded-xl font-bold bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-lg hover:scale-105 transition-all" data-id="element-3114">
                    Confirmă Finalizare
                  </button>}
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}